import React, {
  Suspense,
  lazy,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import AnimeCardsGrid from "../components/AnimeCardsGrid";
import LazyImage from "../components/LazyImage";
import "./AnimePage.css";

const GenreGrid = lazy(() => import("../components/GenreGrid"));

const AnimePage = () => {
  const [sections, setSections] = useState({
    bestOfYear: [],
    topRated: [],
    currentlyAiring: [],
    upcoming: [],
    movies: [],
    genres: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [layout, setLayout] = useState("horizontal");

  // Get current year for the "Best of Year" section
  const currentYear = new Date().getFullYear();

  // Detect screen size and set appropriate card layout
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setLayout("vertical");
      } else {
        setLayout("horizontal");
      }
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchAnimeData = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Check for cached data first
    const cachedData = localStorage.getItem("animePageData");
    const cachedTimestamp = localStorage.getItem("animePageTimestamp");

    // Use cached data if it's less than 30 minutes old
    if (cachedData && cachedTimestamp) {
      const now = Date.now();
      const cacheTime = parseInt(cachedTimestamp);
      const thirtyMinutes = 30 * 60 * 1000;

      if (now - cacheTime < thirtyMinutes) {
        try {
          const parsedData = JSON.parse(cachedData);
          setSections(parsedData);
          setLoading(false);

          // Optional: Fetch in background for next visit
          setTimeout(() => {
            fetchFreshData().catch((err) =>
              console.error("Background fetch error:", err)
            );
          }, 1000);

          return;
        } catch (e) {
          console.error("Error parsing cached data:", e);
          // Continue with normal fetching if parsing fails
        }
      }
    }

    // Proceed with fresh data fetch
    try {
      const sectionData = await fetchFreshData();
      setSections(sectionData);
    } catch (error) {
      console.error("Global fetch error:", error);
      setError("Failed to load anime data. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Separate function for the actual API fetching
  const fetchFreshData = async () => {
    const API_BASE =
      process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

    const endpoints = [
      {
        name: "bestOfYear",
        url: `${API_BASE}/api/anime/sections/best-of-year`,
      },
      { name: "topRated", url: `${API_BASE}/api/anime/sections/top-rated` },
      {
        name: "currentlyAiring",
        url: `${API_BASE}/api/anime/sections/currently-airing`,
      },
      { name: "upcoming", url: `${API_BASE}/api/anime/sections/upcoming` },
      { name: "movies", url: `${API_BASE}/api/anime/sections/movies` },
      { name: "genres", url: `${API_BASE}/api/anime/genres` },
    ];

    // Fetch all endpoints in parallel
    const results = await Promise.all(
      endpoints.map(async (endpoint) => {
        try {
          console.log(`Fetching ${endpoint.name} from ${endpoint.url}...`);

          let url = endpoint.url;
          if (endpoint.name !== "genres") {
            url = `${url}?limit=8`;
          }

          const response = await fetch(url);

          if (!response.ok) {
            console.error(`${endpoint.name} failed:`, response.status);
            return { name: endpoint.name, data: null };
          }

          const responseText = await response.text();

          try {
            const data = JSON.parse(responseText);
            console.log(`${endpoint.name} data received successfully`);
            return { name: endpoint.name, data };
          } catch (parseError) {
            console.error(`Error parsing ${endpoint.name} JSON:`, parseError);
            return { name: endpoint.name, data: null };
          }
        } catch (endpointError) {
          console.error(
            `Network error fetching ${endpoint.name}:`,
            endpointError
          );
          return { name: endpoint.name, data: null };
        }
      })
    );

    // Process results
    const sectionData = {
      bestOfYear: [],
      topRated: [],
      currentlyAiring: [],
      upcoming: [],
      movies: [],
      genres: {},
    };

    results.forEach((result) => {
      if (!result.data) return;

      if (result.name === "genres") {
        // Genres are still an object, no changes needed
        sectionData.genres = result.data;

        // Log some genre info for debugging
        console.log(
          `Genres data received: Object with ${
            Object.keys(result.data).length
          } genre keys`
        );
      } else {
        // Process anime data
        let animeData = [];

        if (Array.isArray(result.data)) {
          // Old format - direct array
          animeData = result.data;
          console.log(
            `${result.name} data received: Array with ${result.data.length} items`
          );
        } else if (result.data?.data && Array.isArray(result.data.data)) {
          // New paginated format
          animeData = result.data.data;
          console.log(
            `${result.name} data received: Paginated with ${result.data.data.length} items`
          );
        } else {
          console.error(
            `Unexpected data format for ${result.name}:`,
            result.data
          );
        }

        sectionData[result.name] = animeData;

        // Log the first item for debugging
        if (animeData.length > 0) {
          console.log(`First ${result.name} item:`, {
            id: animeData[0].id,
            title: animeData[0].title,
          });
        }
      }
    });

    // Store in cache for future visits
    localStorage.setItem("animePageData", JSON.stringify(sectionData));
    localStorage.setItem("animePageTimestamp", Date.now().toString());

    return sectionData;
  };

  useEffect(() => {
    fetchAnimeData();
  }, [fetchAnimeData]);

  const renderSection = (items, title, route) => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return null; // Don't render empty sections
    }

    return (
      <section className="anime-section">
        <div className="section-header">
          <h2 className="section-title">{title}</h2>
          <a href={`/anime/category/${route}`} className="see-more">
            See More
          </a>
        </div>
        <AnimeCardsGrid animes={items.slice(0, 4)} layout={layout} />
      </section>
    );
  };

  // Hero Section Component with randomized featured anime
  const HeroSection = () => {
    // Get up to 10 anime from top rated and currently airing sections
    const topAnimes = [];

    // Add top rated anime (up to 10)
    if (sections.topRated && sections.topRated.length > 0) {
      topAnimes.push(...sections.topRated.slice(0, 10));
    }

    // Add currently airing anime (up to 10)
    if (sections.currentlyAiring && sections.currentlyAiring.length > 0) {
      topAnimes.push(...sections.currentlyAiring.slice(0, 10));
    }

    // Randomly select one anime from the combined array
    // or use fallbacks if the arrays are empty
    let featuredAnime;

    if (topAnimes.length > 0) {
      // Get a random index between 0 and the array length
      const randomIndex = Math.floor(Math.random() * topAnimes.length);
      featuredAnime = topAnimes[randomIndex];
      console.log(
        `Randomly selected anime #${randomIndex} from a pool of ${topAnimes.length} animes`
      );
    } else {
      // Fallback priority if topAnimes is empty
      featuredAnime =
        sections.bestOfYear[0] ||
        sections.topRated[0] ||
        sections.currentlyAiring[0] ||
        sections.movies[0];
      console.log("Using fallback anime for hero section");
    }

    // Capitalize helper function
    const capitalizeFirstLetter = (string) => {
      if (!string) return string;
      return string.charAt(0).toUpperCase() + string.slice(1);
    };

    if (!featuredAnime) {
      return (
        <section className="anime-hero-section">
          <div className="anime-hero-content">
            <div className="anime-hero-text">
              <h1 className="anime-hero-title">Welcome to Anime Universe</h1>
              <p className="anime-hero-synopsis">
                Explore our collection of amazing anime series and movies
              </p>
              <div className="anime-hero-metadata">
                <span>⭐ Start Exploring</span>
              </div>
              <a href="/anime" className="anime-hero-cta">
                Browse All
              </a>
            </div>
            <div className="anime-hero-image-container">
              <img
                src="/default-hero.jpg"
                alt="Anime Universe"
                className="anime-hero-image"
              />
            </div>
          </div>
        </section>
      );
    }

    // Truncate synopsis to a reasonable length and add ellipsis
    const truncateSynopsis = (text, maxLength = 250) => {
      if (!text) return "Explore this amazing anime series...";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength).trim() + "...";
    };

    // Determine if the anime is from top rated or currently airing
    let animeSource = "";
    if (sections.topRated.some((anime) => anime.id === featuredAnime.id)) {
      animeSource = "Top Rated";
    } else if (
      sections.currentlyAiring.some((anime) => anime.id === featuredAnime.id)
    ) {
      animeSource = "Currently Airing";
    } else if (
      sections.bestOfYear.some((anime) => anime.id === featuredAnime.id)
    ) {
      animeSource = `Best of ${currentYear}`;
    }

    return (
      <section className="anime-hero-section">
        <div className="anime-hero-content">
          <div className="anime-hero-text">
            <h1 className="anime-hero-title">
              {featuredAnime.titleEnglish || featuredAnime.title}
            </h1>
            <p className="anime-hero-synopsis">
              {truncateSynopsis(featuredAnime.synopsis)}
            </p>
            <div className="anime-hero-metadata">
              {featuredAnime.score && <span>⭐ {featuredAnime.score}</span>}
              {featuredAnime.episodes && (
                <span>{featuredAnime.episodes} Episodes</span>
              )}
              {featuredAnime.status && <span>{featuredAnime.status}</span>}
              {featuredAnime.season && featuredAnime.year && (
                <span>
                  {capitalizeFirstLetter(featuredAnime.season)}{" "}
                  {featuredAnime.year}
                </span>
              )}
              {animeSource && (
                <span className="featured-source">{animeSource}</span>
              )}
            </div>
            <a href={`/anime/${featuredAnime.id}`} className="anime-hero-cta">
              View Details
            </a>
          </div>
          <div className="anime-hero-image-container">
            <LazyImage
              src={
                featuredAnime.images?.webp?.large_image_url ||
                featuredAnime.images?.jpg?.large_image_url ||
                featuredAnime.images?.webp?.image_url ||
                "/default-hero.jpg"
              }
              alt={featuredAnime.title}
              className="anime-hero-image"
              loading="eager" // Hero image should load immediately
              fallbackSrc="/default-hero.jpg"
            />
          </div>
        </div>
      </section>
    );
  };

  const AnimePageSkeleton = () => (
    <div className="anime-page">
      <div className="anime-page-title-container">
        <h1 className="anime-page-title">Anime</h1>
      </div>

      {/* Hero skeleton */}
      <div className="anime-hero-section skeleton">
        <div className="anime-hero-content">
          <div className="anime-hero-text">
            <div
              className="skeleton-block"
              style={{ height: "40px", width: "70%" }}
            ></div>
            <div
              className="skeleton-block"
              style={{ height: "100px", width: "90%" }}
            ></div>
            <div
              className="skeleton-block"
              style={{ height: "30px", width: "60%" }}
            ></div>
          </div>
          <div className="anime-hero-image-container skeleton-image"></div>
        </div>
      </div>

      {/* Section skeletons */}
      <main className="anime-main">
        {[1, 2, 3].map((i) => (
          <section key={i} className="anime-section skeleton">
            <div className="section-header">
              <div
                className="skeleton-block"
                style={{ height: "24px", width: "40%" }}
              ></div>
            </div>
            <div className="cards-container">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="anime-card skeleton">
                  <div className="skeleton-image"></div>
                  <div
                    className="skeleton-block"
                    style={{ height: "20px", width: "80%" }}
                  ></div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );

  const LazySection = ({ children, title }) => (
    <Suspense
      fallback={
        <section className="anime-section skeleton">
          <div className="section-header">
            <div
              className="skeleton-block"
              style={{ height: "24px", width: "40%" }}
            ></div>
          </div>
          <div className="cards-container">
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="anime-card skeleton">
                <div className="skeleton-image"></div>
                <div
                  className="skeleton-block"
                  style={{ height: "20px", width: "80%" }}
                ></div>
              </div>
            ))}
          </div>
        </section>
      }
    >
      {children}
    </Suspense>
  );

  const IntersectionLoadSection = ({ children }) => {
    const [shouldLoad, setShouldLoad] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        },
        { rootMargin: "200px" } // Start loading when section is 200px from viewport
      );

      if (sectionRef.current) {
        observer.observe(sectionRef.current);
      }

      return () => {
        if (sectionRef.current) {
          observer.disconnect();
        }
      };
    }, []);

    // Skeleton UI while waiting to intersect
    if (!shouldLoad) {
      return (
        <div ref={sectionRef}>
          <section className="anime-section skeleton">
            <div className="section-header">
              <div
                className="skeleton-block"
                style={{ height: "24px", width: "40%" }}
              ></div>
            </div>
            <div className="cards-container">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="anime-card skeleton">
                  <div className="skeleton-image"></div>
                  <div
                    className="skeleton-block"
                    style={{ height: "20px", width: "80%" }}
                  ></div>
                </div>
              ))}
            </div>
          </section>
        </div>
      );
    }

    // Render with suspense once in viewport
    return (
      <div ref={sectionRef}>
        <LazySection>{children}</LazySection>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return <AnimePageSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="error-container">
        <h3>Oops! Something went wrong</h3>
        <p>{error}</p>
        <button className="retry-button" onClick={fetchAnimeData}>
          Try Again
        </button>
      </div>
    );
  }

  // Check if we have any content to display
  const hasContent =
    sections.bestOfYear.length > 0 ||
    sections.topRated.length > 0 ||
    sections.currentlyAiring.length > 0 ||
    sections.upcoming.length > 0 ||
    sections.movies.length > 0 ||
    (sections.genres && Object.keys(sections.genres).length > 0);

  if (!hasContent) {
    return (
      <div className="anime-page">
        <HeroSection />
        <div className="no-results">
          <p>
            No anime content available at the moment. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="anime-page">
      {/* Page Title */}
      <div className="anime-page-title-container">
        <h1 className="anime-page-title">Anime</h1>
      </div>

      <HeroSection />

      {/* Main Content Sections */}
      <main className="anime-main">
        {/* Critical sections loaded immediately */}
        {renderSection(
          sections.bestOfYear,
          `Best of ${currentYear}`,
          "best-of-year"
        )}

        {/* Lazy load below-the-fold sections */}
        <IntersectionLoadSection>
          {renderSection(sections.topRated, "All-Time Top Rated", "top-rated")}
        </IntersectionLoadSection>

        <IntersectionLoadSection>
          {renderSection(
            sections.currentlyAiring,
            "Currently Airing",
            "currently-airing"
          )}
        </IntersectionLoadSection>

        <IntersectionLoadSection>
          {renderSection(sections.upcoming, "Upcoming Anime", "upcoming")}
        </IntersectionLoadSection>

        <IntersectionLoadSection>
          {renderSection(sections.movies, "Anime Movies", "movies")}
        </IntersectionLoadSection>

        {/* Genre Grid Section with lazy loading */}
        <Suspense
          fallback={
            <section className="genres-section skeleton">
              <div className="section-header">
                <div
                  className="skeleton-block"
                  style={{ height: "24px", width: "40%" }}
                ></div>
              </div>
              <div className="skeleton-grid">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="genre-card skeleton">
                    <div
                      className="skeleton-block"
                      style={{ height: "100%", width: "100%" }}
                    ></div>
                  </div>
                ))}
              </div>
            </section>
          }
        >
          <section className="genres-section">
            <div className="section-header">
              <h2 className="section-title">Browse by Genre</h2>
              <a href="/genres" className="see-more">
                View All Genres
              </a>
            </div>
            <GenreGrid genres={sections.genres} />
          </section>
        </Suspense>
      </main>
    </div>
  );
};

export default AnimePage;
